import { FC, ReactNode } from 'react'
import { alpha, Box, ButtonBase, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material'
import {
  AccountBalanceWalletOutlined,
  ArrowBack,
  AssignmentOutlined,
  AttachFileOutlined,
  DescriptionOutlined,
  EqualizerOutlined,
  HomeOutlined,
  KeyboardArrowRight
} from '@mui/icons-material'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import BackNavigationHeader, { BackNavigationContent } from 'components/BackNavigationHeader'
import OperationsFrozenMessage from 'components/OperationsFrozenMessage'
import AdditionalServicesAverageUtilityConsumption from './AdditionalServicesAverageUtilityConsumption'
import { useLeaseContract } from 'services/contracts/hooks/useLeaseContracts'
import { useLeaseContractsMediaMetersList } from 'services/contracts/hooks/useLeaseContractsMediaMetersList'
import { useLeaseContractProtocols } from 'services/protocols/protocols.hooks'

import { getLeaseStatusMeta } from './leases.utils'

interface MenuItem {
  key: string
  icon: ReactNode
  tint: string
  iconBg?: string
  title: string
  description: string
  onClick: () => void
}

// Lease contract detail ("Panel Klienta 3.0"): hero + action tiles + media usage.
const AdditionalServicesMedia: FC = () => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const navigate = useNavigate()
  const { id } = useParams()
  const { leaseContract } = useLeaseContract(Number(id))
  const { leaseContractsMediaMetersList } = useLeaseContractsMediaMetersList(Number(id))
  const { protocolsList } = useLeaseContractProtocols(Number(id))

  // WIS: uproszczone fakturowanie — nie pokazujemy klientowi kwot za media (CB-3130).
  const isWisContract = !!leaseContract?.isWisContract

  const statusMeta = leaseContract ? getLeaseStatusMeta(leaseContract.leaseStatus) : null
  const dates = leaseContract
    ? `${dayjs(leaseContract.leaseStart).format('DD.MM.YYYY')} — ${dayjs(
        leaseContract.effectiveLeaseEnd || leaseContract.leaseEnd
      ).format('DD.MM.YYYY')}`
    : ''

  const menuItems: MenuItem[] = [
    {
      key: 'preview',
      icon: <DescriptionOutlined sx={{ fontSize: isDesktop ? '24px' : '20px' }} />,
      tint: theme.r4r.navy,
      title: formatMessage({ id: 'additionalServicesMedia.preview', defaultMessage: 'Podgląd umowy' }),
      description: formatMessage({
        id: 'additionalServicesMedia.previewDesc',
        defaultMessage: 'Pełna treść dokumentu PDF'
      }),
      onClick: () => window.open(leaseContract?.leaseContractFile, '_blank')
    },
    {
      key: 'annexes',
      icon: <AttachFileOutlined sx={{ fontSize: isDesktop ? '24px' : '20px' }} />,
      tint: theme.r4r.navy,
      title: formatMessage({ id: 'additionalServicesMedia.annexes', defaultMessage: 'Aneksy' }),
      description: formatMessage({
        id: 'additionalServicesMedia.annexesDesc',
        defaultMessage: 'Aneksy do umowy'
      }),
      onClick: () => navigate(`/additional-services/${id}/annexes`)
    },
    {
      key: 'schedule',
      icon: <AccountBalanceWalletOutlined sx={{ fontSize: isDesktop ? '24px' : '20px' }} />,
      tint: '#D4A800',
      iconBg: alpha(theme.r4r.yellow, 0.1),
      title: formatMessage({
        id: 'additionalServicesMedia.payInvoice',
        defaultMessage: 'Opłać / Podgląd FV'
      }),
      description: formatMessage({
        id: 'additionalServicesMedia.scheduleDesc',
        defaultMessage: 'Faktury i harmonogram opłat'
      }),
      onClick: () => navigate(`/additional-services/${id}/payment-schedule`)
    },
    {
      key: 'information',
      icon: <HomeOutlined sx={{ fontSize: isDesktop ? '24px' : '20px' }} />,
      tint: theme.r4r.navy,
      title: formatMessage({
        id: 'additionalServicesMedia.unitInfo',
        defaultMessage: 'Info o lokalu'
      }),
      description: formatMessage({
        id: 'additionalServicesMedia.informationDesc',
        defaultMessage: 'Kody dostępu, WiFi, informacje o lokalu'
      }),
      onClick: () => navigate(`/additional-services/${id}/information`)
    },
    ...(protocolsList.length
      ? [
          {
            key: 'protocol',
            icon: <AssignmentOutlined sx={{ fontSize: isDesktop ? '24px' : '20px' }} />,
            tint: theme.r4r.navy,
            title: formatMessage({
              id: 'acceptanceProtocolListItem.protocol',
              defaultMessage: 'Protokół Z-O'
            }),
            description: formatMessage({
              id: 'additionalServicesMedia.protocolDesc',
              defaultMessage: 'Protokół zdawczo-odbiorczy'
            }),
            onClick: () => navigate(`/additional-services/${id}/protocol`)
          }
        ]
      : [])
  ]

  const statusChip = !!statusMeta && (
    <Box
      sx={{
        backgroundColor: isDesktop ? alpha(theme.r4r.cyan, 0.25) : statusMeta.colors.backgroundColor,
        padding: isDesktop ? '4px 14px' : '4px 12px',
        flexShrink: 0
      }}>
      <Typography
        sx={{
          color: isDesktop ? '#fff' : statusMeta.colors.color,
          fontSize: '11px',
          fontWeight: 700,
          letterSpacing: isDesktop ? 0 : '0.4px',
          whiteSpace: 'nowrap'
        }}>
        {formatMessage(statusMeta.i18n)}
      </Typography>
    </Box>
  )

  const tileGrid = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px',
        width: '100%'
      }}>
      {menuItems.map((item) => (
        <ButtonBase
          key={item.key}
          onClick={item.onClick}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '11.5px',
            padding: '28px 16px',
            backgroundColor: '#fff',
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
            ':hover': { backgroundColor: '#F9FAFB' }
          }}>
          <Box
            sx={{
              width: '52px',
              height: '52px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: item.iconBg || alpha(theme.r4r.navy, 0.06),
              color: item.tint
            }}>
            {item.icon}
          </Box>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 600,
              lineHeight: '15.6px',
              color: theme.r4r.ink,
              textAlign: 'center'
            }}>
            {item.title}
          </Typography>
        </ButtonBase>
      ))}
    </Box>
  )

  const menuCard = (
    <Box sx={{ border: `1px solid ${theme.r4r.greyLight}`, backgroundColor: '#fff' }}>
      {menuItems.map((item, index) => (
        <ButtonBase
          key={item.key}
          onClick={item.onClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            textAlign: 'left',
            gap: '14px',
            padding: '16px',
            borderBottom:
              index === menuItems.length - 1 ? 'none' : `1px solid ${theme.r4r.greyLight}`,
            ':hover': { backgroundColor: alpha(theme.r4r.azure, 0.06) }
          }}>
          <Box
            sx={{
              width: '40px',
              height: '40px',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: item.iconBg || alpha(item.tint, 0.14),
              color: item.tint
            }}>
            {item.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: theme.r4r.navy }}>
              {item.title}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: theme.r4r.grey, mt: '2px' }}>
              {item.description}
            </Typography>
          </Box>
          <KeyboardArrowRight sx={{ color: theme.r4r.grey, fontSize: '20px' }} />
        </ButtonBase>
      ))}
    </Box>
  )

  const mediaSection = (
    <Box
      sx={{
        mt: isDesktop ? 0 : '24px',
        backgroundColor: '#fff',
        boxShadow: isDesktop ? '0px 1px 3px rgba(0, 0, 0, 0.06)' : theme.r4r.cardShadow,
        padding: isDesktop ? '40px 24px 36px' : '20px'
      }}>
      {isDesktop && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
            mb: leaseContractsMediaMetersList.length ? '16px' : '14px'
          }}>
          <EqualizerOutlined sx={{ fontSize: '16px', color: theme.r4r.navy }} />
          <Typography sx={{ fontSize: '13px', fontWeight: 700, color: theme.r4r.navy }}>
            {formatMessage({ id: 'additionalServicesMedia.mediaUsage', defaultMessage: 'Zużycie mediów' })}
          </Typography>
        </Box>
      )}
      {!isDesktop && (
        <Typography sx={{ fontSize: '15px', fontWeight: 700, color: theme.r4r.navy, mb: '12px' }}>
          {formatMessage({ id: 'additionalServicesMedia.mediaUsage', defaultMessage: 'Zużycie mediów' })}
        </Typography>
      )}
      <AdditionalServicesAverageUtilityConsumption embedded />
    </Box>
  )

  if (isDesktop) {
    return (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: '20px' }}>
          <IconButton
            onClick={() => navigate('/additional-services')}
            sx={{ width: '36px', height: '36px', color: theme.r4r.navy, borderRadius: 0 }}>
            <ArrowBack sx={{ fontSize: '22px' }} />
          </IconButton>
          <Typography
            sx={{ fontSize: '24px', fontWeight: 700, lineHeight: '28.8px', color: theme.r4r.navy }}>
            {formatMessage({
              id: 'additionalServicesMedia.detailsTitle',
              defaultMessage: 'Szczegóły umowy'
            })}
          </Typography>
        </Box>
        {leaseContract?.isOperationsFrozen && <OperationsFrozenMessage variant='banner' />}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '20px'
          }}>
          <Box
            sx={{
              background: 'linear-gradient(142deg, #1F1F73 0%, #6A8AE5 100%)',
              padding: '28px 32px',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '16px'
            }}>
            <Box>
              <Typography sx={{ fontSize: '22px', fontWeight: 800, color: '#fff' }}>
                {leaseContract?.contractNumber}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#fff', opacity: 0.6, mt: '4px' }}>
                {dates}
              </Typography>
            </Box>
            {statusChip}
          </Box>
          {tileGrid}
          {!isWisContract && mediaSection}
        </Box>
      </>
    )
  }

  return (
    <>
      <BackNavigationHeader
        label={formatMessage({
          id: 'additionalServicesMedia.detailsTitle',
          defaultMessage: 'Szczegóły umowy'
        })}
        navigateTo='/additional-services'
      />
      <BackNavigationContent>
        {leaseContract?.isOperationsFrozen && <OperationsFrozenMessage variant='banner' />}
        <Box
          sx={{
            background: theme.r4r.gradient,
            boxShadow: theme.r4r.cardShadow,
            padding: '20px',
            mb: '16px'
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
                {leaseContract?.contractNumber}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: alpha('#fff', 0.75), mt: '4px' }}>
                {dates}
              </Typography>
            </Box>
            {statusChip}
          </Box>
        </Box>
        {menuCard}
        {!isWisContract && mediaSection}
      </BackNavigationContent>
    </>
  )
}

export default AdditionalServicesMedia
